import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, Gamepad2, Package } from 'lucide-react';
import * as imageService from '../services/imageService';
import PlaygroundConfigEditor from './PlaygroundConfigEditor';

// Standalone Playground Config page.
//
// Playground configs are stored on the curated image's D1 record, but the only
// way to edit them used to be through ClusterSharingModal — reachable only from
// a live cluster row (a subscribed NATS worker). This page edits the image
// record directly, so configs can be authored with nothing running.

const countConfigs = (pgc) => {
  if (Array.isArray(pgc)) return pgc.filter(c => c && c.enabled).length;
  if (pgc && typeof pgc === 'object' && pgc.enabled) return 1;
  return 0;
};

const PlaygroundConfigPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await imageService.listCuratedImages();
      if (data.success) {
        const imgs = data.images || [];
        setImages(imgs);
        // Keep selection if still present, else default to the first image
        setSelectedId(prev => (prev && imgs.some(i => i.id === prev)) ? prev : (imgs[0]?.id ?? null));
      } else {
        setError(data.error || 'Failed to load images');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const filtered = search
    ? images.filter(img =>
        (img.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (img.docker_image || '').toLowerCase().includes(search.toLowerCase()))
    : images;

  const selected = images.find(img => img.id === selectedId) || null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gamepad2 size={22} className="text-purple-600" />
            Playground Config
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure how curated images appear in the Playground. Edits apply to the
            image itself — no running cluster or worker required.
          </p>
        </div>
        <button
          onClick={fetchImages}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="flex gap-4 h-[calc(100vh-220px)] min-h-[400px]">
        {/* LEFT: image list */}
        <div className="w-72 shrink-0 border border-gray-200 rounded-lg bg-white flex flex-col overflow-hidden">
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search images..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2 text-gray-400">
                <RefreshCw size={20} className="animate-spin" />
                <span className="text-xs">Loading images...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">
                {images.length === 0 ? 'No curated images found' : 'No matches'}
              </div>
            ) : (
              filtered.map(img => {
                const n = countConfigs(img.playground_config);
                return (
                  <button
                    key={img.id}
                    onClick={() => setSelectedId(img.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg mb-0.5 border transition-colors ${
                      selectedId === img.id
                        ? 'bg-purple-50 border-purple-200'
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-sm text-gray-900 truncate">{img.name}</span>
                      {n > 0 ? (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-purple-100 text-purple-700 shrink-0">
                          {n} cfg{n > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-gray-100 text-gray-400 shrink-0">
                          none
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 font-mono truncate mt-0.5">
                      {img.tcf_image || img.docker_image}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT: editor */}
        <div className="flex-1 border border-gray-200 rounded-lg bg-white overflow-y-auto">
          {selected ? (
            <div className="p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <Package size={16} className="text-gray-400" />
                <div>
                  <div className="font-semibold text-gray-900">{selected.name}</div>
                  <div className="text-[11px] text-gray-400 font-mono">{selected.tcf_image || selected.docker_image}</div>
                </div>
              </div>
              <PlaygroundConfigEditor image={selected} onSaved={fetchImages} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
              <Gamepad2 size={32} />
              <span className="text-sm">Select an image to configure its playground</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaygroundConfigPage;
